import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { package as PackagePropType } from 'lib/CustomPropTypes';
import LeadBorderTop from 'components/LeadBorderTop';
import PackageTitle from 'components/PackageTitle';
import TeaseCard from 'components/TeaseCard';
import TeasePicture from 'components/TeasePicture';
import TitleLogo from 'components/TitleLogo';
import AIMS_FLAVORS from 'lib/aimsFlavors';

function getPicture(item, index, packageType) {
  const defaultSizes = {
    s: AIMS_FLAVORS.FOCAL_758x379,
    m: AIMS_FLAVORS.FOCAL_460x460,
  };

  const alternateSizes = {
    ...defaultSizes,
    l: AIMS_FLAVORS.FOCAL_460x230,
    xl: AIMS_FLAVORS.FOCAL_580x290,
  };

  let sizes = alternateSizes;
  if (packageType === 'fourUp' || (packageType === 'threeUp' && index > 0)) {
    sizes = defaultSizes;
  }

  return (
    <TeasePicture
      responsiveFlavors={sizes}
      type={item.type}
      computedValues={item.computedValues}
    />
  );
}

function getTease(item, index, packageType, packageMetadata, vertical, isMobileOnlyComponent) {
  const { id, metadata, type } = item || {};
  let teaseStyles;
  if (type === 'liveBlog') {
    item.related = []; // eslint-disable-line
  }

  switch (packageType) {
    case 'fourUp':
      teaseStyles = 'multi-up__tease-card--four-up';
      break;

    case 'threeUp':
      if (index === 0) {
        teaseStyles = 'multi-up__tease-card--three-up-main';
        break;
      }
      teaseStyles = 'multi-up__tease-card--three-up';
      break;

    default:
      teaseStyles = 'multi-up__tease-card--two-up';
      break;
  }

  const teaseMetadata = metadata;
  const adStyles = type === 'ad' ? 'boxinlineAd' : ''; // hide ad div on Desktop

  return item && (
    <div
      key={`${id}-${index}`}
      className={classNames('multi-up__article', adStyles, teaseStyles)}
      data-test="multi-up__item"
    >
      <TeaseCard
        key={id}
        content={item}
        vertical={vertical}
        pkgType={packageType}
        isMobileOnlyComponent={isMobileOnlyComponent}
        {...packageMetadata}
        {...teaseMetadata}
      >
        {getPicture(item, index, packageType)}
      </TeaseCard>
    </div>
  );
}

function MultiUp(props) {
  const {
    alternateTitleFormat,
    className,
    content,
    pkgClassName,
    vertical,
    isFirstLayout,
    isMobileOnlyComponent,
  } = props;

  if (!content || !(content.items && content.items[0])) return null;

  const {
    id, type, items, metadata = {}, packageIndex,
  } = content;

  const classes = classNames(
    pkgClassName,
    `pkg-index-${packageIndex + 1}`,
    className,
    {
      'multi-up__has-title': metadata && metadata.title,
      'multi-up__two-up': type === 'twoUp',
      'multi-up__three_up': type === 'threeUp',
      'multi-up__four-up': type === 'fourUp',
    },
  );

  const { title, logoUrl } = metadata;

  let ModuleTitle;
  if (logoUrl) {
    ModuleTitle = <TitleLogo logoUrl={logoUrl} />;
  } else if (title) {
    ModuleTitle = (
      <PackageTitle
        alternateDisplayFormat={alternateTitleFormat}
        metadata={metadata}
      />
    );
  }

  const isFirstPackageInFirstLayout = isFirstLayout && !packageIndex;
  const articlesClasses = classNames(
    'multi-up__articles',
    {
      'multi-up__accent-line': isFirstPackageInFirstLayout,
    },
  );

  return (
    <section data-packageid={id} className={classes} data-test={`${type}__section`}>
      <LeadBorderTop types={['collectionLead']} />
      <div className="multi-up__content">
        {ModuleTitle}
        <div className={classNames(articlesClasses, 'bg-knockout-primary')}>
          { items.map((item, index) => getTease(item,
            index, type, metadata, vertical, isMobileOnlyComponent)) }
        </div>
      </div>
    </section>
  );
}

MultiUp.propTypes = {
  alternateTitleFormat: PropTypes.bool,
  content: PackagePropType.isRequired,
  className: PropTypes.string,
  pkgClassName: PropTypes.string,
  vertical: PropTypes.string,
  isFirstLayout: PropTypes.bool,
  isMobileOnlyComponent: PropTypes.bool,
};

MultiUp.defaultProps = {
  alternateTitleFormat: false,
  className: null,
  pkgClassName: 'pkg multiUp',
  vertical: '',
  isFirstLayout: false,
  isMobileOnlyComponent: false,
};

export default MultiUp;
